/**
 * Schema utility composable
 * Provides reusable functions for manipulating form schemas
 * Compatible with both Options API and Composition API
 */

/**
 * Rearranges schema keys based on their sequence property
 * Fields without a sequence property will be sorted last
 *
 * @param {Object} formConfig - Form configuration object containing schema
 * @param {Object} formConfig.schema - Schema object with field definitions
 * @returns {Object} Form configuration with sorted schema
 */
export const rearrangeSchemaBySequence = (formConfig) => {
  if (!formConfig || !formConfig.schema) return formConfig

  const schemaEntries = Object.entries(formConfig.schema)
  const sortedEntries = schemaEntries.sort((a, b) => {
    const seqA = a[1].sequence || Infinity
    const seqB = b[1].sequence || Infinity
    return seqA - seqB
  })

  const sortedSchema = {}
  sortedEntries.forEach(([key, value]) => {
    sortedSchema[key] = value
  })

  return {
    ...formConfig,
    schema: sortedSchema
  }
}

/**
 * Composable function for schema utilities
 * Use this in Composition API setup() function
 *
 * @example
 * // In Composition API (Vue 3)
 * import { useSchemaUtils } from '@/composables/useSchemaUtils'
 *
 * export default {
 *   setup() {
 *     const { rearrangeSchemaBySequence } = useSchemaUtils()
 *     const sortedConfig = rearrangeSchemaBySequence(formConfig)
 *     return { sortedConfig }
 *   }
 * }
 *
 * @example
 * // In Options API (Vue 2)
 * import { rearrangeSchemaBySequence } from '@/composables/useSchemaUtils'
 *
 * export default {
 *   methods: {
 *     processSchema() {
 *       this.schema = rearrangeSchemaBySequence(this.schema)
 *     }
 *   }
 * }
 *
 * @returns {Object} Schema utility functions
 */
export const useSchemaUtils = () => {
  return {
    rearrangeSchemaBySequence
  }
}
